import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, MapPin, AlertTriangle, Truck, RefreshCw, Info, User, Package, Gift } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabaseFunctionErrors";

// Common countries list
const COUNTRIES = [
  { code: "IN", name: "India" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "SG", name: "Singapore" },
  { code: "JP", name: "Japan" },
  { code: "NZ", name: "New Zealand" },
  { code: "NL", name: "Netherlands" },
  { code: "IT", name: "Italy" },
  { code: "ES", name: "Spain" },
  { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" },
  { code: "BE", name: "Belgium" },
  { code: "AT", name: "Austria" },
  { code: "DK", name: "Denmark" },
  { code: "NO", name: "Norway" },
  { code: "IE", name: "Ireland" },
  { code: "PT", name: "Portugal" },
  { code: "PL", name: "Poland" },
  { code: "FI", name: "Finland" },
  { code: "MX", name: "Mexico" },
  { code: "BR", name: "Brazil" },
  { code: "ZA", name: "South Africa" },
  { code: "MY", name: "Malaysia" },
  { code: "TH", name: "Thailand" },
  { code: "PH", name: "Philippines" },
  { code: "ID", name: "Indonesia" },
  { code: "VN", name: "Vietnam" },
  { code: "KR", name: "South Korea" },
  { code: "HK", name: "Hong Kong" },
  { code: "TW", name: "Taiwan" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "QA", name: "Qatar" },
  { code: "KW", name: "Kuwait" },
  { code: "BH", name: "Bahrain" },
  { code: "OM", name: "Oman" },
];

// States/Provinces by country
const STATES_BY_COUNTRY: Record<string, string[]> = {
  "India": [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
    "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
    "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
    "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
    "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
  ],
  "United States": [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine",
    "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
    "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
    "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
    "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
    "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia",
    "Washington", "West Virginia", "Wisconsin", "Wyoming", "District of Columbia"
  ],
  "United Kingdom": [
    "England", "Scotland", "Wales", "Northern Ireland"
  ],
  "Canada": [
    "Alberta", "British Columbia", "Manitoba", "New Brunswick",
    "Newfoundland and Labrador", "Northwest Territories", "Nova Scotia",
    "Nunavut", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Yukon"
  ],
  "Australia": [
    "Australian Capital Territory", "New South Wales", "Northern Territory",
    "Queensland", "South Australia", "Tasmania", "Victoria", "Western Australia"
  ],
  "United Arab Emirates": [
    "Abu Dhabi", "Ajman", "Dubai", "Fujairah", "Ras Al Khaimah", "Sharjah", "Umm Al Quwain"
  ],
  "Germany": [
    "Baden-Württemberg", "Bavaria", "Berlin", "Brandenburg", "Bremen", "Hamburg",
    "Hesse", "Lower Saxony", "Mecklenburg-Vorpommern", "North Rhine-Westphalia",
    "Rhineland-Palatinate", "Saarland", "Saxony", "Saxony-Anhalt",
    "Schleswig-Holstein", "Thuringia"
  ],
  "France": [
    "Auvergne-Rhône-Alpes", "Bourgogne-Franche-Comté", "Brittany", "Centre-Val de Loire",
    "Corsica", "Grand Est", "Hauts-de-France", "Île-de-France", "Normandy",
    "Nouvelle-Aquitaine", "Occitanie", "Pays de la Loire", "Provence-Alpes-Côte d'Azur"
  ],
  "Japan": [
    "Hokkaido", "Aomori", "Iwate", "Miyagi", "Akita", "Yamagata", "Fukushima",
    "Ibaraki", "Tochigi", "Gunma", "Saitama", "Chiba", "Tokyo", "Kanagawa",
    "Niigata", "Toyama", "Ishikawa", "Fukui", "Yamanashi", "Nagano", "Gifu",
    "Shizuoka", "Aichi", "Mie", "Shiga", "Kyoto", "Osaka", "Hyogo", "Nara",
    "Wakayama", "Tottori", "Shimane", "Okayama", "Hiroshima", "Yamaguchi",
    "Tokushima", "Kagawa", "Ehime", "Kochi", "Fukuoka", "Saga", "Nagasaki",
    "Kumamoto", "Oita", "Miyazaki", "Kagoshima", "Okinawa"
  ],
  "Mexico": [
    "Aguascalientes", "Baja California", "Baja California Sur", "Campeche",
    "Chiapas", "Chihuahua", "Coahuila", "Colima", "Durango", "Guanajuato",
    "Guerrero", "Hidalgo", "Jalisco", "México", "Mexico City", "Michoacán",
    "Morelos", "Nayarit", "Nuevo León", "Oaxaca", "Puebla", "Querétaro",
    "Quintana Roo", "San Luis Potosí", "Sinaloa", "Sonora", "Tabasco",
    "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán", "Zacatecas"
  ],
  "Brazil": [
    "Acre", "Alagoas", "Amapá", "Amazonas", "Bahia", "Ceará", "Distrito Federal",
    "Espírito Santo", "Goiás", "Maranhão", "Mato Grosso", "Mato Grosso do Sul",
    "Minas Gerais", "Pará", "Paraíba", "Paraná", "Pernambuco", "Piauí",
    "Rio de Janeiro", "Rio Grande do Norte", "Rio Grande do Sul", "Rondônia",
    "Roraima", "Santa Catarina", "São Paulo", "Sergipe", "Tocantins"
  ],
  "South Africa": [
    "Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal", "Limpopo",
    "Mpumalanga", "North West", "Northern Cape", "Western Cape"
  ],
  "Malaysia": [
    "Johor", "Kedah", "Kelantan", "Kuala Lumpur", "Labuan", "Melaka",
    "Negeri Sembilan", "Pahang", "Penang", "Perak", "Perlis", "Putrajaya",
    "Sabah", "Sarawak", "Selangor", "Terengganu"
  ],
  "Saudi Arabia": [
    "Riyadh", "Makkah", "Madinah", "Eastern Province", "Asir", "Tabuk",
    "Hail", "Northern Borders", "Jazan", "Najran", "Al-Bahah", "Al-Jawf", "Qassim"
  ],
};

interface ShippingAddress {
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  province: string;
  country: string;
  zip: string;
  phone: string;
  email: string;
}

interface OrganizerDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface PricingResult {
  currency?: string;
  cartItems?: Array<{ title?: string; image?: string | null; quantity?: number; price?: number; variantTitle?: string }>;
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
}

interface ShippingAddressFormProps {
  sessionToken: string | null;
  currency?: string;
  onAddressValidated: (
    address: ShippingAddress, 
    pricing: PricingResult, 
    warning?: string | null,
    organizerDetails?: OrganizerDetails,
    giftOptions?: { isGift: boolean; giftMessage: string; giftRecipientName: string }
  ) => void;
  initialEmail?: string;
  isGift?: boolean;
  onIsGiftChange?: (isGift: boolean) => void;
  giftMessage?: string;
  onGiftMessageChange?: (message: string) => void;
  giftRecipientName?: string;
  onGiftRecipientNameChange?: (name: string) => void;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function ShippingAddressForm({ 
  sessionToken, 
  currency,
  onAddressValidated,
  initialEmail = "",
  isGift = false,
  onIsGiftChange,
  giftMessage = "",
  onGiftMessageChange,
  giftRecipientName = "",
  onGiftRecipientNameChange,
}: ShippingAddressFormProps) {
  // Organizer (your) details
  const [organizerDetails, setOrganizerDetails] = useState<OrganizerDetails>({
    firstName: "",
    lastName: "",
    email: initialEmail,
    phone: "",
  });

  // Delivery recipient details
  const [address, setAddress] = useState<ShippingAddress>({
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    city: "",
    province: "",
    country: "India",
    zip: "",
    phone: "",
    email: "",
  });

  // Same as organizer checkbox
  const [sameAsOrganizer, setSameAsOrganizer] = useState(true);

  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<"validation" | "notServiceable" | "retryable" | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  // IMPORTANT: Helper functions must be defined BEFORE they are used to prevent TDZ errors in production builds
  // Get postal code label based on country
  function getPostalCodeLabel(country: string): string {
    switch (country) {
      default: return "Zip Code / Postal Code";
    }
  }

  // Get province label based on country
  function getProvinceLabel(country: string): string {
    switch (country) {
      case "India": return "State";
      case "United States": return "State";
      case "United Kingdom": return "County";
      case "Canada": return "Province";
      case "Australia": return "State/Territory";
      case "Japan": return "Prefecture";
      case "United Arab Emirates": return "Emirate";
      default: return "State/Province/Region";
    }
  }

  // Field-level validation errors
  function getFieldErrors() {
    const errors: Record<string, string> = {};
    
    // Organizer fields
    if (!organizerDetails.firstName.trim()) {
      errors.organizerFirstName = "First name is required";
    }
    if (!organizerDetails.lastName.trim()) {
      errors.organizerLastName = "Last name is required";
    }
    if (!organizerDetails.email.trim()) {
      errors.organizerEmail = "Email is required";
    } else if (!organizerDetails.email.includes("@")) {
      errors.organizerEmail = "Please enter a valid email";
    }
    if (!organizerDetails.phone.trim()) {
      errors.organizerPhone = "Phone number is required";
    }

    // Recipient/Address fields
    if (!address.firstName.trim()) {
      errors.recipientFirstName = "First name is required";
    }
    if (!address.lastName.trim()) {
      errors.recipientLastName = "Last name is required";
    }
    if (!address.email.trim()) {
      errors.recipientEmail = "Email is required";
    } else if (!address.email.includes("@")) {
      errors.recipientEmail = "Please enter a valid email";
    }
    if (!address.phone.trim()) {
      errors.recipientPhone = "Phone number is required";
    }
    if (!address.address1.trim()) {
      errors.address1 = "Address is required";
    }
    if (!address.city.trim()) {
      errors.city = "City is required";
    }
    if (!address.country) {
      errors.country = "Country is required";
    }
    if (!address.province.trim()) {
      errors.province = `${getProvinceLabel(address.country)} is required`;
    }
    if (!address.zip.trim()) {
      errors.zip = `${getPostalCodeLabel(address.country)} is required`;
    } else if (address.zip.length < 3 || address.zip.length > 12) {
      errors.zip = `Please enter a valid ${getPostalCodeLabel(address.country).toLowerCase()}`;
    }
    
    return errors;
  }

  const fieldErrors = getFieldErrors();
  const hasErrors = Object.keys(fieldErrors).length > 0;

  // Handle "same as organizer" checkbox
  function handleSameAsOrganizer(checked: boolean) {
    setSameAsOrganizer(checked);
    if (checked) {
      setAddress(prev => ({
        ...prev,
        firstName: organizerDetails.firstName,
        lastName: organizerDetails.lastName,
        phone: organizerDetails.phone,
        email: organizerDetails.email,
      }));
    }
  }

  useEffect(() => {
    if (!sameAsOrganizer) {
      return;
    }

    setAddress((prev) => ({
      ...prev,
      firstName: organizerDetails.firstName,
      lastName: organizerDetails.lastName,
      phone: organizerDetails.phone,
      email: organizerDetails.email,
    }));
  }, [
    organizerDetails.email,
    organizerDetails.firstName,
    organizerDetails.lastName,
    organizerDetails.phone,
    sameAsOrganizer,
  ]);

  // Client-side validation
  const validateForm = (): { valid: boolean; message?: string } => {
    // Validate organizer details
    if (!organizerDetails.firstName.trim()) {
      return { valid: false, message: "Please enter your first name" };
    }
    if (!organizerDetails.lastName.trim()) {
      return { valid: false, message: "Please enter your last name" };
    }
    if (!organizerDetails.email.trim() || !organizerDetails.email.includes("@")) {
      return { valid: false, message: "Please enter a valid email address" };
    }
    if (!organizerDetails.phone.trim()) {
      return { valid: false, message: "Please enter your phone number" };
    }

    // Validate delivery address
    if (!address.firstName.trim()) {
      return { valid: false, message: "Please enter recipient's first name" };
    }
    if (!address.lastName.trim()) {
      return { valid: false, message: "Please enter recipient's last name" };
    }
    if (!address.email.trim() || !address.email.includes("@")) {
      return { valid: false, message: "Please enter recipient's email address" };
    }
    if (!address.phone.trim()) {
      return { valid: false, message: "Please enter recipient's phone number" };
    }
    if (!address.address1.trim()) {
      return { valid: false, message: "Please enter delivery address" };
    }
    if (!address.city.trim()) {
      return { valid: false, message: "Please enter city" };
    }
    if (!address.country) {
      return { valid: false, message: "Please select country" };
    }
    if (!address.province.trim()) {
      return { valid: false, message: `Please ${address.country === "India" ? "select" : "enter"} ${getProvinceLabel(address.country).toLowerCase()}` };
    }
    if (!address.zip.trim()) {
      return { valid: false, message: `Please enter ${getPostalCodeLabel(address.country).toLowerCase()}` };
    }
    // Basic postal code validation
    if (address.zip.length < 3 || address.zip.length > 12) {
      return { valid: false, message: `Please enter a valid ${getPostalCodeLabel(address.country).toLowerCase()}` };
    }
    return { valid: true };
  };

  const isFormValid = validateForm().valid;

  const handleValidateAddress = async () => {
    // Show field errors on submit attempt
    setShowFieldErrors(true);
    
    const validation = validateForm();
    if (!validation.valid) {
      // No need to set error message - red field highlighting is sufficient
      setErrorType("validation");
      
      // Scroll to first error field
      setTimeout(() => {
        const errorKeys = Object.keys(fieldErrors);
        if (errorKeys.length > 0) {
          // Map field error keys to input IDs
          const fieldIdMap: Record<string, string> = {
            organizerFirstName: 'organizer-firstName',
            organizerLastName: 'organizer-lastName',
            organizerEmail: 'organizer-email',
            organizerPhone: 'organizer-phone',
            recipientFirstName: 'firstName',
            recipientLastName: 'lastName',
            recipientEmail: 'recipient-email',
            recipientPhone: 'recipient-phone',
            address1: 'address1',
            city: 'city',
            country: 'country',
            province: 'province',
            zip: 'zip',
          };
          const firstErrorField = fieldIdMap[errorKeys[0]];
          if (firstErrorField) {
            const element = document.getElementById(firstErrorField);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.focus();
            }
          }
        }
      }, 100);
      return;
    }

    if (!sessionToken) {
      setError("Still setting up your session. Please wait a moment and try again.");
      return;
    }

    setIsValidating(true);
    setError(null);
    setErrorType(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('validate-shipping-address', {
        body: {
          sessionToken,
          currency,
          address,
          email: address.email, // Use recipient's email for order updates
          organizerEmail: organizerDetails.email,
        },
      });

      if (fnError) {
        throw fnError;
      }

      if (data.error) {
        if (data.notServiceable) {
          setErrorType("notServiceable");
          setError(data.details || "This address is not serviceable by the merchant.");
        } else if (data.validationFailed) {
          setErrorType("validation");
          setError(data.details || "Please check your address details.");
        } else if (data.retryable) {
          setErrorType("retryable");
          setError(data.details || "Something went wrong. Please try again.");
          setRetryCount(prev => prev + 1);
        } else {
          setErrorType("validation");
          setError(data.error);
        }
        return;
      }

      if (data.success && data.pricing) {
        // Pass any shipping warning to the parent, along with organizer details and gift options
        const warning = data.shippingWarning || data.shippingNotes || null;
        onAddressValidated(address, data.pricing, warning, organizerDetails, { isGift, giftMessage, giftRecipientName });
      }

    } catch (err: any) {
      console.error("Error validating address:", err);
      setErrorType("retryable");
      const errorMessage = await getSupabaseFunctionErrorMessage(
        err,
        "Unable to validate your address right now. Please try again.",
      );
      setError(errorMessage);
      setRetryCount(prev => prev + 1);
    } finally {
      setIsValidating(false);
    }
  };

  // Handle postal code input - allow alphanumeric for international codes
  const handlePostalCodeChange = (value: string) => {
    const cleaned = value.replace(/[^a-zA-Z0-9\s-]/g, "").toUpperCase().slice(0, 12);
    setAddress({ ...address, zip: cleaned });
    if (error && errorType === "validation") {
      setError(null);
      setErrorType(null);
    }
  };

  // Auto-format phone
  const handlePhoneChange = (field: "organizer" | "recipient", value: string) => {
    const cleaned = value.replace(/[^\d+]/g, "");
    if (field === "organizer") {
      setOrganizerDetails({ ...organizerDetails, phone: cleaned });
    } else {
      setAddress({ ...address, phone: cleaned });
    }
  };

  return (
    <div className="space-y-6">
      {/* Your Details (Organizer) */}
      <Card className="border-primary/20">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5 text-primary" />
            Your Details
          </CardTitle>
          <CardDescription>
            Enter your contact information (the person organizing this split payment)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="organizer-firstName" className={cn(showFieldErrors && fieldErrors.organizerFirstName && "text-destructive")}>
                Your First Name *
              </Label>
              <Input
                id="organizer-firstName"
                placeholder="First name"
                value={organizerDetails.firstName}
                className={cn(showFieldErrors && fieldErrors.organizerFirstName && "border-destructive focus-visible:ring-destructive")}
                onChange={(e) => {
                  setOrganizerDetails({ ...organizerDetails, firstName: e.target.value });
                  if (error) setError(null);
                }}
              />
              {showFieldErrors && fieldErrors.organizerFirstName && (
                <p className="text-xs text-destructive">{fieldErrors.organizerFirstName}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="organizer-lastName" className={cn(showFieldErrors && fieldErrors.organizerLastName && "text-destructive")}>
                Your Last Name *
              </Label>
              <Input
                id="organizer-lastName"
                placeholder="Last name"
                value={organizerDetails.lastName}
                className={cn(showFieldErrors && fieldErrors.organizerLastName && "border-destructive focus-visible:ring-destructive")}
                onChange={(e) => {
                  setOrganizerDetails({ ...organizerDetails, lastName: e.target.value });
                  if (error) setError(null);
                }}
              />
              {showFieldErrors && fieldErrors.organizerLastName && (
                <p className="text-xs text-destructive">{fieldErrors.organizerLastName}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="organizer-email" className={cn(showFieldErrors && fieldErrors.organizerEmail && "text-destructive")}>
                Your Email *
              </Label>
              <Input
                id="organizer-email"
                type="email"
                placeholder="your@email.com"
                value={organizerDetails.email}
                className={cn(showFieldErrors && fieldErrors.organizerEmail && "border-destructive focus-visible:ring-destructive")}
                onChange={(e) => {
                  setOrganizerDetails({ ...organizerDetails, email: e.target.value });
                  if (error) setError(null);
                }}
              />
              {showFieldErrors && fieldErrors.organizerEmail && (
                <p className="text-xs text-destructive">{fieldErrors.organizerEmail}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="organizer-phone" className={cn(showFieldErrors && fieldErrors.organizerPhone && "text-destructive")}>
                Your Phone *
              </Label>
              <Input
                id="organizer-phone"
                type="tel"
                placeholder="+91 98765 43210"
                value={organizerDetails.phone}
                className={cn(showFieldErrors && fieldErrors.organizerPhone && "border-destructive focus-visible:ring-destructive")}
                onChange={(e) => handlePhoneChange("organizer", e.target.value)}
              />
              {showFieldErrors && fieldErrors.organizerPhone && (
                <p className="text-xs text-destructive">{fieldErrors.organizerPhone}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delivery Address (Recipient) */}
      <Card className="border-primary/20">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5 text-primary" />
            Delivery Details
          </CardTitle>
          <CardDescription>
            Enter the recipient's details and delivery address
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Same as organizer checkbox */}
          <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
            <Checkbox
              id="same-as-organizer"
              checked={sameAsOrganizer}
              onCheckedChange={(checked) => handleSameAsOrganizer(checked === true)}
            />
            <Label htmlFor="same-as-organizer" className="text-sm cursor-pointer">
              Deliver to myself (use my details above)
            </Label>
          </div>

          {/* Recipient Email */}
          <div className="space-y-2">
            <Label htmlFor="recipient-email" className={cn(showFieldErrors && fieldErrors.recipientEmail && "text-destructive")}>
              Recipient's Email *
            </Label>
            <Input
              id="recipient-email"
              type="email"
              placeholder="recipient@email.com"
              value={address.email}
              className={cn(showFieldErrors && fieldErrors.recipientEmail && "border-destructive focus-visible:ring-destructive")}
              onChange={(e) => {
                setAddress({ ...address, email: e.target.value });
                if (sameAsOrganizer) setSameAsOrganizer(false);
                if (error) setError(null);
              }}
            />
            {showFieldErrors && fieldErrors.recipientEmail && (
              <p className="text-xs text-destructive">{fieldErrors.recipientEmail}</p>
            )}
            <p className="text-xs text-muted-foreground">Order updates will be sent to this email</p>
          </div>

          {/* Recipient Name */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName" className={cn(showFieldErrors && fieldErrors.recipientFirstName && "text-destructive")}>
                Recipient's First Name *
              </Label>
              <Input
                id="firstName"
                placeholder="First name"
                value={address.firstName}
                className={cn(showFieldErrors && fieldErrors.recipientFirstName && "border-destructive focus-visible:ring-destructive")}
                onChange={(e) => {
                  setAddress({ ...address, firstName: e.target.value });
                  if (sameAsOrganizer) setSameAsOrganizer(false);
                  if (error) setError(null);
                }}
              />
              {showFieldErrors && fieldErrors.recipientFirstName && (
                <p className="text-xs text-destructive">{fieldErrors.recipientFirstName}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className={cn(showFieldErrors && fieldErrors.recipientLastName && "text-destructive")}>
                Recipient's Last Name *
              </Label>
              <Input
                id="lastName"
                placeholder="Last name"
                value={address.lastName}
                className={cn(showFieldErrors && fieldErrors.recipientLastName && "border-destructive focus-visible:ring-destructive")}
                onChange={(e) => {
                  setAddress({ ...address, lastName: e.target.value });
                  if (sameAsOrganizer) setSameAsOrganizer(false);
                  if (error) setError(null);
                }}
              />
              {showFieldErrors && fieldErrors.recipientLastName && (
                <p className="text-xs text-destructive">{fieldErrors.recipientLastName}</p>
              )}
            </div>
          </div>

          {/* Recipient Phone */}
          <div className="space-y-2">
            <Label htmlFor="recipient-phone" className={cn(showFieldErrors && fieldErrors.recipientPhone && "text-destructive")}>
              Recipient's Phone *
            </Label>
            <Input
              id="recipient-phone"
              type="tel"
              placeholder="+91 98765 43210"
              value={address.phone}
              className={cn(showFieldErrors && fieldErrors.recipientPhone && "border-destructive focus-visible:ring-destructive")}
              onChange={(e) => {
                handlePhoneChange("recipient", e.target.value);
                if (sameAsOrganizer) setSameAsOrganizer(false);
              }}
            />
            {showFieldErrors && fieldErrors.recipientPhone && (
              <p className="text-xs text-destructive">{fieldErrors.recipientPhone}</p>
            )}
            <p className="text-xs text-muted-foreground">For delivery updates and courier contact</p>
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              Delivery Address
            </h4>

            {/* Gift Option - immediately below heading */}
            <div className="rounded-lg border bg-muted/30 p-4 space-y-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Gift className="h-5 w-5 text-primary" />
                  <div>
                    <Label htmlFor="gift-switch" className="font-medium">Is this a gift?</Label>
                    <p className="text-sm text-muted-foreground">Add a personal message for the recipient</p>
                  </div>
                </div>
                <Switch
                  id="gift-switch"
                  checked={isGift}
                  onCheckedChange={onIsGiftChange}
                />
              </div>
              
              {isGift && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="gift-recipient">Recipient's Name</Label>
                    <Input
                      id="gift-recipient"
                      placeholder="Who is this gift for?"
                      value={giftRecipientName}
                      onChange={(e) => onGiftRecipientNameChange?.(e.target.value)}
                      maxLength={100}
                    />
                    <p className="text-xs text-muted-foreground">This will be shown to friends in their invitation emails</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gift-message">Gift Message (optional)</Label>
                    <Textarea
                      id="gift-message"
                      placeholder="Write your gift message for the recipient..."
                      value={giftMessage}
                      onChange={(e) => onGiftMessageChange?.(e.target.value)}
                      className="min-h-[80px]"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Country */}
            <div className="space-y-2 mb-4">
              <Label htmlFor="country" className={cn(showFieldErrors && fieldErrors.country && "text-destructive")}>
                Country *
              </Label>
              <Select
                value={address.country}
                onValueChange={(value) => {
                  setAddress({ ...address, country: value, province: "", zip: "" });
                  if (error) setError(null);
                }}
              >
                <SelectTrigger id="country" className={cn(showFieldErrors && fieldErrors.country && "border-destructive")}>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country.code} value={country.name}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {showFieldErrors && fieldErrors.country && (
                <p className="text-xs text-destructive">{fieldErrors.country}</p>
              )}
            </div>

            {/* Address Line 1 */}
            <div className="space-y-2 mb-4">
              <Label htmlFor="address1" className={cn(showFieldErrors && fieldErrors.address1 && "text-destructive")}>
                Address Line 1 *
              </Label>
              <Input
                id="address1"
                placeholder="House no, Building, Street"
                value={address.address1}
                className={cn(showFieldErrors && fieldErrors.address1 && "border-destructive focus-visible:ring-destructive")}
                onChange={(e) => {
                  setAddress({ ...address, address1: e.target.value });
                  if (error) setError(null);
                }}
              />
              {showFieldErrors && fieldErrors.address1 && (
                <p className="text-xs text-destructive">{fieldErrors.address1}</p>
              )}
            </div>

            {/* Address Line 2 */}
            <div className="space-y-2 mb-4">
              <Label htmlFor="address2">Address Line 2 (Optional)</Label>
              <Input
                id="address2"
                placeholder="Apartment, Landmark"
                value={address.address2}
                onChange={(e) => setAddress({ ...address, address2: e.target.value })}
              />
            </div>

            {/* City and Postal Code */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="space-y-2">
                <Label htmlFor="city" className={cn(showFieldErrors && fieldErrors.city && "text-destructive")}>
                  City *
                </Label>
                <Input
                  id="city"
                  placeholder="City"
                  value={address.city}
                  className={cn(showFieldErrors && fieldErrors.city && "border-destructive focus-visible:ring-destructive")}
                  onChange={(e) => {
                    setAddress({ ...address, city: e.target.value });
                    if (error) setError(null);
                  }}
                />
                {showFieldErrors && fieldErrors.city && (
                  <p className="text-xs text-destructive">{fieldErrors.city}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip" className={cn(showFieldErrors && fieldErrors.zip && "text-destructive")}>
                  {getPostalCodeLabel(address.country)} *
                </Label>
                <Input
                  id="zip"
                  placeholder={getPostalCodeLabel(address.country)}
                  value={address.zip}
                  maxLength={12}
                  onChange={(e) => handlePostalCodeChange(e.target.value)}
                  className={cn(showFieldErrors && fieldErrors.zip && "border-destructive focus-visible:ring-destructive")}
                />
                {showFieldErrors && fieldErrors.zip && (
                  <p className="text-xs text-destructive">{fieldErrors.zip}</p>
                )}
              </div>
            </div>

            {/* State/Province */}
            <div className="space-y-2">
              <Label htmlFor="province" className={cn(showFieldErrors && fieldErrors.province && "text-destructive")}>
                {getProvinceLabel(address.country)} *
              </Label>
              {address.country === "India" ? (
                <Select
                  value={address.province}
                  onValueChange={(value) => {
                    setAddress({ ...address, province: value });
                    if (error) setError(null);
                  }}
                >
                  <SelectTrigger
                    id="province"
                    className={cn(showFieldErrors && fieldErrors.province && "border-destructive focus:ring-destructive")}
                  >
                    <SelectValue placeholder={`Select ${getProvinceLabel(address.country).toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {STATES_BY_COUNTRY[address.country].map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="province"
                  placeholder={getProvinceLabel(address.country)}
                  value={address.province}
                  onChange={(e) => {
                    setAddress({ ...address, province: e.target.value });
                    if (error) setError(null);
                  }}
                  className={cn(showFieldErrors && fieldErrors.province && "border-destructive focus-visible:ring-destructive")}
                />
              )}
              {showFieldErrors && fieldErrors.province && (
                <p className="text-xs text-destructive">{fieldErrors.province}</p>
              )}
            </div>
          </div>


          {/* Error Alert - only show for server errors, not validation errors (red highlighting handles those) */}
          {error && errorType !== "validation" && (
            <Alert 
              variant={errorType === "notServiceable" ? "destructive" : "default"} 
              className={
                errorType === "notServiceable" 
                  ? "" 
                  : "border-amber-500 bg-amber-50"
              }
            >
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {errorType === "notServiceable" 
                  ? "Delivery Not Available" 
                  : "Connection Issue"
                }
              </AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Info about verification */}
          <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
            <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              We'll verify the delivery address with the merchant to ensure delivery is available.
            </p>
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleValidateAddress}
            disabled={isValidating}
            className="w-full"
            size="lg"
          >
            {isValidating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Validating Address...
              </>
            ) : errorType === "retryable" && retryCount > 0 ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </>
            ) : (
              <>
                <Truck className="h-4 w-4 mr-2" />
                Calculate Shipping & Continue
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
